import { api } from '../../lib/api.ts';
import { dv } from '../../i18n/dv.ts';

// A private notebook per person per meeting: one meeting-wide general pad (the
// page the chair's script is rendered into) plus a page per approved agenda
// item. Never shared with anyone else — the backend enforces that every one of
// these calls can only ever reach the caller's own pages.
export interface Note {
  body: string;
  updatedAt: string | null;
}

/**
 * Which page of the notebook a route is on. `'general'` is the meeting-wide
 * pad; anything else is an agenda item id. Kept as a single string because it
 * is also the `$pageId` URL segment.
 */
export type PageId = string;
export const GENERAL_PAGE: PageId = 'general';

export interface NotePage {
  /** '' for the general pad, else the agenda item id. */
  agendaItemId: string;
  /** «2.1» etc. Empty for the general pad and for off-agenda pages. */
  number: string;
  /** The agenda item's title; null for the general pad, which is UI copy. */
  title: string | null;
  /** False once the item has left the approved agenda but the page survives. */
  onAgenda: boolean;
  hasNote: boolean;
  updatedAt: string | null;
}

/** The `$pageId` URL segment for a page from the list. */
export function pageIdOf(page: NotePage): PageId {
  return page.agendaItemId === '' ? GENERAL_PAGE : page.agendaItemId;
}

/** The label for a page: the general pad's is UI copy, an item's is its title. */
export function pageLabel(page: NotePage): string {
  if (page.agendaItemId === '') return dv.notes.generalPage;
  const title = page.title ?? '';
  return page.number === '' ? title : `${page.number} ${title}`;
}

// Query keys live here so every surface that reads a note — the page, and the
// full-screen preview that can now page through the whole notebook — hits the
// SAME cache entry instead of refetching each other's documents.
//
// Both are scoped to the VIEWER as well as the meeting: notes are private, and
// `ssoBootstrap(true)` can swap the authenticated user in place with no reload
// and no cache clear, so on a shared council-office machine an unscoped key
// would hand user A's note to user B.
export const noteQueryKey = (userId: string | null, meetingId: string, pageId: PageId): unknown[] =>
  ['meetingNote', userId, meetingId, pageId];

export const pagesQueryKey = (userId: string | null, meetingId: string): unknown[] =>
  ['meetingNotePages', userId, meetingId];

// The general pad keeps its original URL, so its endpoints are untouched; item
// pages hang off /notes/items/:agendaItemId.
const noteUrl = (meetingId: string, pageId: PageId): string =>
  pageId === GENERAL_PAGE
    ? `/meetings/${meetingId}/notes`
    : `/meetings/${meetingId}/notes/items/${pageId}`;

export const notesApi = {
  // The caller's own page list — the general pad, a page per approved agenda
  // item in script numbering, then any page whose item has left the agenda that
  // they have already written on. Carries no page bodies.
  async fetchPages(meetingId: string): Promise<NotePage[]> {
    const { data } = await api.get<{ pages: NotePage[] }>(`/meetings/${meetingId}/notes/pages`);
    return data.pages;
  },
  // Returns `{ body: '', updatedAt: null }` (a 200) when no note exists yet —
  // never a 404. The editor opens blank.
  async fetchNote(meetingId: string, pageId: PageId): Promise<Note> {
    const { data } = await api.get<{ note: Note }>(noteUrl(meetingId, pageId));
    return data.note;
  },
  async saveNote(meetingId: string, pageId: PageId, body: string): Promise<Note> {
    const { data } = await api.put<{ note: Note }>(noteUrl(meetingId, pageId), { body });
    return data.note;
  },
  // Read-only render of the chair's script — writes nothing. Used by the chair's
  // manual regenerate action, which targets the GENERAL pad only: the script is
  // one continuous document covering the whole meeting.
  async fetchScript(meetingId: string): Promise<string> {
    const { data } = await api.get<{ script: string }>(`/meetings/${meetingId}/notes/script`);
    return data.script;
  },
};
